import Highcharts from "highcharts";
import { FormatHelpers } from "../helpers/format-helpers";
import { IndicatorHelpers } from "../helpers/indicator-helper";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { IndicatorType } from "../models/enum/indicator-type.enum";
import { IComponent } from "./component.interface";

export class TooltipComponent implements IComponent {
    public id = '';

    private element: HTMLElement;
    private base: IChartContainer;
    private lastPrice = 0;
    private isLastBar = false;

    constructor(element: HTMLElement, base: IChartContainer) {
        this.element = element;
        this.base = base;
        this.subscribeLastPrice();
    }
    
    public show() {
        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        const nonChartMode = chartStyle?.nonNormalChartMode();
        if (!this.base.tiChartSeries || !this.base.tiChartSeries.chartStyleSettings || nonChartMode) {
            return;
        }

        this.element.addClass('active');
    }

    public hide() {
        this.element.removeClass('active');
    }

    public isActive(): boolean {
        return this.element.containClass('active');
    }

    public setPrice(price: number) {
        this.element.findFirst('.price .v')?.setInnerHtml(FormatHelpers.numberToString(price));
    }

    public renderTooltip(xValue: number) {
        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        const nonChartMode = chartStyle?.nonNormalChartMode();
        if (!this.base.tiChartSeries || !this.base.tiChartSeries.chartStyleSettings || nonChartMode) {
            return;
        }
        
        if (!this.allDrawingToolHidden()) {
            return;
        }

        const point = this.base.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == xValue);
        if (!point) {
            return;
        }

        this.isLastBar = this.base.tiChartSeries.lastDataPoint?.xValue == xValue;
        let close = point.close;
        if (this.isLastBar && this.lastPrice) {
            close = this.lastPrice;
        }

        this.element.findFirst('.date .v')?.setInnerHtml(Highcharts.dateFormat('%m-%d-%Y', point.timestamp));
        this.element.findFirst('.open .v')?.setInnerHtml(FormatHelpers.numberToString(point.open));
        this.element.findFirst('.high .v')?.setInnerHtml(FormatHelpers.numberToString(point.high));
        this.element.findFirst('.low .v')?.setInnerHtml(FormatHelpers.numberToString(point.low));
        this.element.findFirst('.close .v')?.setInnerHtml(FormatHelpers.numberToString(close));

        if (TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe)) {
            this.element.findFirst('.time')?.removeClass('hidden');
            this.element.findFirst('.time .v')?.setInnerHtml(Highcharts.dateFormat('%I:%M', point.timestamp));
        } else {
            this.element.findFirst('.time')?.addClass('hidden');
        }
        
        let indicatorsHTML = '';
        const allIndicators = this.base.tiChartSeries.getAllActiveIndicatorOptions();
        for (const indicator of allIndicators) {
            const findIndicatorsResult = IndicatorHelpers.findIndicatorPoints(this.base.tiChartSeries.tiIndicatorsData, indicator, point.xValue);
            if (!findIndicatorsResult.length) {
                continue;
            }

            indicatorsHTML += '<span class="bl">';
            if (indicator.value == IndicatorType.Volume) {
                const volume = this.base.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == point.xValue)?.volume ?? 0;
                indicatorsHTML += `<span class="lb" style="color:${findIndicatorsResult[0].color};">VOLUME</span> <span class="v">${FormatHelpers.numberToString(volume)}</span>`;
            } else if (indicator.value == IndicatorType.AverageVolume) {
                const volumeAvg = this.base.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == point.xValue)?.volumeAvg ?? 0;
                indicatorsHTML += `<span class="lb" style="color:${findIndicatorsResult[0].color};">AVG_VOLUME</span> <span class="v">${FormatHelpers.numberToString(volumeAvg)}</span>`;
            } else {
                for (const result of findIndicatorsResult) {
                    indicatorsHTML += `<span class="lb" style="color:${result.color};">${result.name}</span> <span class="v">${FormatHelpers.numberToString(result.value)}</span>`;
                }
            }
            indicatorsHTML += '</span>';
        }

        this.element.findFirst('.indicators')?.setInnerHtml(indicatorsHTML);
    }

    private subscribeLastPrice() {        
        this.base.chartService.subscribeLastPriceData((price: number) => {
            this.lastPrice = price;
            if (this.isLastBar && this.isActive()) {
                this.element.findFirst('.close .v')?.setInnerHtml(FormatHelpers.numberToString(this.lastPrice));
            }
        });
    }

    private allDrawingToolHidden(): boolean {
        const points = Array.from(this.base.tiChartSeries.element.querySelectorAll<HTMLElement>('.highcharts-control-points path'));
        return points.every((x: HTMLElement) => x.getAttribute('visibility') == 'hidden');
    }
}